import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, CheckCircle2, Undo2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { AdminTitle } from "@/admin/AdminLayout";
import { StatusBadge } from "@/pages/ContributePage";

const TABS = [["pending", "Awaiting approval"], ["approved", "Published"], ["rejected", "Returned"]];

export default function AdminSubmissions() {
  const { refresh } = useSite();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/submissions", { params: { status: tab } }).then((r) => setItems(r.data));
  useEffect(() => { setItems(null); load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const approve = (a) => act(() => api.post(`/admin/submissions/${a.id}/approve`), `"${a.title}" published in Readers' Chronicles`);
  const reject = (a) => {
    const note = window.prompt("Note to the author (optional) — what should they change?");
    if (note !== null) act(() => api.post(`/admin/submissions/${a.id}/reject`, { note }), "Returned to the author");
  };

  return (
    <div data-testid="admin-submissions">
      <AdminTitle eyebrow="From the readers" title="Reader Submissions" />
      <p className="mb-6 italic text-[#4a3a2a]">Readers' articles are only published — in the Readers' Chronicles section — once you approve them. Readers' private drafts are not shown here.</p>
      <div className="flex border border-[#c9a227] w-fit max-w-full overflow-x-auto mb-6">
        {TABS.map(([k, l]) => (
          <button key={k} data-testid={`submissions-tab-${k}`} onClick={() => setTab(k)} className={`px-5 max-sm:px-3 py-2 font-heading text-xs font-bold uppercase tracking-wider ${tab === k ? "bg-[#991b1b] text-[#fdf6e3]" : "hover:bg-[#c9a227]/15"}`}>{l}</button>
        ))}
      </div>
      <div className="vellum">
        {items?.map((a) => (
          <div key={a.id} data-testid={`submission-${a.slug}`} className="flex flex-wrap items-center gap-4 px-4 sm:px-6 py-4 border-b border-[#c9a227]/25">
            <div className="flex-1 min-w-[14rem]">
              <p className="font-heading font-bold">{a.title}</p>
              <p className="text-sm text-[#5e4a35]">by {a.author_name} · {new Date(a.updated_at).toLocaleString()}</p>
              {a.summary && <p className="text-sm mt-1 line-clamp-2">{a.summary}</p>}
            </div>
            <StatusBadge status={a.review_status} />
            <div className="flex flex-wrap gap-2">
              <Link to={`/preview/${a.id}`} target="_blank" data-testid={`submission-preview-${a.slug}`} className="btn-ghost-illum !py-2 !px-3"><Eye className="w-4 h-4" /> Preview</Link>
              {a.review_status !== "approved" && <button onClick={() => approve(a)} data-testid={`submission-approve-${a.slug}`} className="btn-illum theme-frost !py-2 !px-3"><CheckCircle2 className="w-4 h-4" /> Approve</button>}
              {a.review_status !== "rejected" && <button onClick={() => reject(a)} data-testid={`submission-reject-${a.slug}`} className="btn-ghost-illum !py-2 !px-3 !text-[#991b1b]"><Undo2 className="w-4 h-4" /> {a.review_status === "approved" ? "Unpublish" : "Return"}</button>}
              <button onClick={() => window.confirm(`Delete "${a.title}" permanently?`) && act(() => api.delete(`/articles/${a.id}`), "Deleted")} data-testid={`submission-delete-${a.slug}`} className="btn-ghost-illum !py-2 !px-3 !text-[#991b1b]" title="Delete permanently"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items?.length === 0 && <p data-testid="submissions-empty" className="p-8 text-center italic">Nothing here.</p>}
      </div>
    </div>
  );
}
